import Vue from "vue";
import type { NavigationGuardNext, Route } from "vue-router";
import { AuthProviderType } from "@/util/authProviderInterface";
import { getToken, hasPendingReturnUrl, hasStoredSession, markSignInFailed, redirectToLogin } from "@/util/cognitoAuth";
import { trackNavigationWait } from "@/util/navigationProgress";

/** Where a signed-in user who opens the site root is sent. */
export const LANDING_PATH = "/processing";

const AUTH_ERROR_ROUTE = { path: "/unauthorized", query: { authError: "1" }, replace: true };

/**
 * - `exchangeCode`: the hosted Cognito login just returned a code to trade for tokens.
 * - `signIn`: a Cognito tenant with no session; the hosted login is the only way in.
 * - `authError`: the hosted Cognito login returned an error instead of a code.
 * - `showLogin`: an MSAL/OIDC visitor with no account, who signs in from the page's button.
 */
export type HomeDecision = "landing" | "exchangeCode" | "signIn" | "authError" | "showLogin";

export interface HomeAuthState {
    provider: AuthProviderType;
    hasCognitoSession: boolean;
    hasCognitoCode: boolean;
    hasCognitoError: boolean;
    msalAuthenticated: boolean;
}

export function decideHomeRoute(state: HomeAuthState): HomeDecision {
    if (state.provider === "Cognito") {
        // Sending this visitor back to the hosted login can return the same error straight away.
        if (state.hasCognitoError) return "authError";
        // A returning code is the newer sign-in; stored tokens may belong to an earlier user.
        if (state.hasCognitoCode) return "exchangeCode";
        return state.hasCognitoSession ? "landing" : "signIn";
    }

    return state.msalAuthenticated ? "landing" : "showLogin";
}

/**
 * beforeEnter for the site root. It decides before anything renders, so a signed-in user goes
 * straight to the landing page instead of watching the sign-in page while the session is checked.
 * Redirects replace the entry: Back must not return to "/", and never to a spent "?code=".
 * Where next() is never called, the browser is leaving and the startup splash stays up.
 */
export async function routeFromHome(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
    try {
        const provider: AuthProviderType = Vue.prototype.$authProviderType;
        const decision = decideHomeRoute({
            provider,
            hasCognitoSession: hasStoredSession(),
            hasCognitoCode: !!to.query.code,
            hasCognitoError: !!to.query.error,
            // $msal is installed only for MSAL and OIDC tenants.
            msalAuthenticated: provider !== "Cognito" && Vue.prototype.$msal.isAuthenticated(),
        });

        switch (decision) {
            case "landing":
                next({ path: LANDING_PATH, replace: true });
                return;
            case "exchangeCode": {
                // A sign-in started from a deep link reloads there once the code is exchanged.
                const reloading = hasPendingReturnUrl();
                await trackNavigationWait(getToken());
                if (!reloading) next({ path: LANDING_PATH, replace: true });
                return;
            }
            case "signIn":
                // The code comes back to "/", which lands the user from here without a reload.
                await redirectToLogin(null);
                return;
            case "authError":
                console.error("The hosted login returned an error:", to.query.error, to.query.error_description);
                markSignInFailed();
                next(AUTH_ERROR_ROUTE);
                return;
            case "showLogin":
                next();
                return;
        }
    } catch (error) {
        console.error("Signing in from the home page failed:", error);
        markSignInFailed();
        next(AUTH_ERROR_ROUTE);
    }
}
